import { combineReducers } from 'redux';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import * as Sentry from '@sentry/react';
import { sentryReduxState } from '../services/helpers/SentryFilters';
import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import ActiveTabSlice from './slices/ActiveTabSlice';
import CurrentCompanySlice from './slices/CurrentCompanySlice';
import CurrentFranchiseSlice from './slices/CurrentFranchiseSlice';
import CurrentServiceProviderSlice from './slices/CurrentServiceProviderSlice';
import ExpandedProductsSlice from './slices/ExpandedProductsSlice';
import InitialDataSlice from './slices/InitialDataSlice';
import PendingIntegrationsSlice from './slices/PendingIntegrationsSlice';
import SideBarSlice from './slices/SideBarSlice';
import StorefrontSlice from './slices/StorefrontSlice';
import TestRestaurantsSlice from './slices/TestRestaurantsSlice';
import AppStatusSlice from './slices/AppStatusSlice';
import {
  businessDocumentsApiSlice,
  businessManagersApiSlice,
  cashbackProgramApiSlice,
  categoriesApiSlice,
  currentCompanyApiSlice,
  currentCompanyInfoApiSlice,
  customersApiSlice,
  deliveriesApiSlice,
  featuredPostsSlice,
  franchisesApiSlice,
  giftCardsApiSlice,
  integrationsApiSlice,
  managersApiSlice,
  menusApiSlice,
  mobileAppsSlice,
  modifierGroupsApiSlice,
  modifiersApiSlice,
  offersApiSlice,
  paymentGatewaysApiSlice,
  phoneAnsweringApiSlice,
  productsApiSlice,
  promoCodesApiSlice,
  salesSummaryApiSlice,
  serviceContractsApiSlice,
  serviceProviderApiSlice,
  serviceProviderStatsApiSlice,
  smartPricingApiSlice,
  subscriptionsApiSlice,
  taxCategoriesApiSlice,
  taxSettingsApiSlice,
} from './api-slices';

const apiReducerPath = [
  categoriesApiSlice.reducerPath,
  deliveriesApiSlice.reducerPath,
  managersApiSlice.reducerPath,
  menusApiSlice.reducerPath,
  modifierGroupsApiSlice.reducerPath,
  modifiersApiSlice.reducerPath,
  featuredPostsSlice.reducerPath,
  phoneAnsweringApiSlice.reducerPath,
  productsApiSlice.reducerPath,
  offersApiSlice.reducerPath,
  mobileAppsSlice.reducerPath,
  serviceProviderStatsApiSlice.reducerPath,
  serviceProviderApiSlice.reducerPath,
  subscriptionsApiSlice.reducerPath,
  serviceContractsApiSlice.reducerPath,
  giftCardsApiSlice.reducerPath,
  franchisesApiSlice.reducerPath,
  currentCompanyApiSlice.reducerPath,
  currentCompanyInfoApiSlice.reducerPath,
  businessManagersApiSlice.reducerPath,
  businessDocumentsApiSlice.reducerPath,
  promoCodesApiSlice.reducerPath,
  smartPricingApiSlice.reducerPath,
  customersApiSlice.reducerPath,
  paymentGatewaysApiSlice.reducerPath,
  cashbackProgramApiSlice.reducerPath,
  integrationsApiSlice.reducerPath,
  salesSummaryApiSlice.reducerPath,
  taxCategoriesApiSlice.reducerPath,
  taxSettingsApiSlice.reducerPath,
];

const persistConfig = {
  key: 'root',
  storage,
  blacklist: apiReducerPath,
};

export const reducersCombined = combineReducers({
  activeTab: ActiveTabSlice.reducer,
  currentCompany: CurrentCompanySlice.reducer,
  currentFranchise: CurrentFranchiseSlice.reducer,
  currentServiceProvider: CurrentServiceProviderSlice.reducer,
  expandedProducts: ExpandedProductsSlice.reducer,
  initialData: InitialDataSlice.reducer,
  pendingIntegrations: PendingIntegrationsSlice.reducer,
  sideBar: SideBarSlice.reducer,
  storefront: StorefrontSlice.reducer,
  testRestaurants: TestRestaurantsSlice.reducer,
  appStatus: AppStatusSlice.reducer,

  [categoriesApiSlice.reducerPath]: categoriesApiSlice.reducer,
  [deliveriesApiSlice.reducerPath]: deliveriesApiSlice.reducer,
  [managersApiSlice.reducerPath]: managersApiSlice.reducer,
  [menusApiSlice.reducerPath]: menusApiSlice.reducer,
  [modifierGroupsApiSlice.reducerPath]: modifierGroupsApiSlice.reducer,
  [modifiersApiSlice.reducerPath]: modifiersApiSlice.reducer,
  [featuredPostsSlice.reducerPath]: featuredPostsSlice.reducer,
  [phoneAnsweringApiSlice.reducerPath]: phoneAnsweringApiSlice.reducer,
  [productsApiSlice.reducerPath]: productsApiSlice.reducer,
  [mobileAppsSlice.reducerPath]: mobileAppsSlice.reducer,
  [offersApiSlice.reducerPath]: offersApiSlice.reducer,
  [serviceProviderStatsApiSlice.reducerPath]: serviceProviderStatsApiSlice.reducer,
  [serviceProviderApiSlice.reducerPath]: serviceProviderApiSlice.reducer,
  [subscriptionsApiSlice.reducerPath]: subscriptionsApiSlice.reducer,
  [serviceContractsApiSlice.reducerPath]: serviceContractsApiSlice.reducer,
  [giftCardsApiSlice.reducerPath]: giftCardsApiSlice.reducer,
  [franchisesApiSlice.reducerPath]: franchisesApiSlice.reducer,
  [currentCompanyApiSlice.reducerPath]: currentCompanyApiSlice.reducer,
  [currentCompanyInfoApiSlice.reducerPath]: currentCompanyInfoApiSlice.reducer,
  [businessManagersApiSlice.reducerPath]: businessManagersApiSlice.reducer,
  [businessDocumentsApiSlice.reducerPath]: businessDocumentsApiSlice.reducer,
  [promoCodesApiSlice.reducerPath]: promoCodesApiSlice.reducer,
  [smartPricingApiSlice.reducerPath]: smartPricingApiSlice.reducer,
  [customersApiSlice.reducerPath]: customersApiSlice.reducer,
  [paymentGatewaysApiSlice.reducerPath]: paymentGatewaysApiSlice.reducer,
  [cashbackProgramApiSlice.reducerPath]: cashbackProgramApiSlice.reducer,
  [integrationsApiSlice.reducerPath]: integrationsApiSlice.reducer,
  [salesSummaryApiSlice.reducerPath]: salesSummaryApiSlice.reducer,
  [taxCategoriesApiSlice.reducerPath]: taxCategoriesApiSlice.reducer,
  [taxSettingsApiSlice.reducerPath]: taxSettingsApiSlice.reducer,
});

const rootReducer = (state, action) => {
  if (action.type === 'RESET_STORE') {
    return reducersCombined(undefined, action);
  }

  return reducersCombined(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const enhancer = Sentry.createReduxEnhancer({
  actionTransformer: (action) => {
    try {
      return action.type?.startsWith('initialData/') ? null : action;
    } catch (e) {
      return null;
    }
  },
  stateTransformer: sentryReduxState,
});

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // TODO: remove once api manager is reworked
        ignoredActions: ['persist/PERSIST'],
        ignoredPaths: apiReducerPath,
      },
    }).concat(
      categoriesApiSlice.middleware,
      deliveriesApiSlice.middleware,
      managersApiSlice.middleware,
      menusApiSlice.middleware,
      modifierGroupsApiSlice.middleware,
      modifiersApiSlice.middleware,
      featuredPostsSlice.middleware,
      phoneAnsweringApiSlice.middleware,
      productsApiSlice.middleware,
      mobileAppsSlice.middleware,
      offersApiSlice.middleware,
      serviceProviderStatsApiSlice.middleware,
      serviceProviderApiSlice.middleware,
      subscriptionsApiSlice.middleware,
      serviceContractsApiSlice.middleware,
      giftCardsApiSlice.middleware,
      franchisesApiSlice.middleware,
      currentCompanyApiSlice.middleware,
      currentCompanyInfoApiSlice.middleware,
      businessManagersApiSlice.middleware,
      businessDocumentsApiSlice.middleware,
      promoCodesApiSlice.middleware,
      smartPricingApiSlice.middleware,
      customersApiSlice.middleware,
      paymentGatewaysApiSlice.middleware,
      cashbackProgramApiSlice.middleware,
      integrationsApiSlice.middleware,
      salesSummaryApiSlice.middleware,
      taxCategoriesApiSlice.middleware,
      taxSettingsApiSlice.middleware,
    ),
  enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(enhancer),
  devTools: import.meta.env.VITE_APP_MODE !== 'production',
});

// Enables RTK Query's refetchOnFocus / refetchOnReconnect behaviour.
setupListeners(store.dispatch);

export const persistor = persistStore(store);
